import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useStateContext } from '../../../contexts/ContextProvider';
import { usePermissions } from '../../../contexts/PermissionContext';
import { usePanne } from '../../api/usePannes';
import { useTraductionsMetier } from '../../api/useTraductionsMetier';
import EtatErreur from '../../components/EtatErreur';
import Screen from '../../components/Screen';
import Text from '../../components/Text';
import { heurePanne, libellePriorite, libelleStatutPanne, lieuPanne } from '../../helpers/maintenance';
import { echecSansDonnee } from '../../helpers/requete';
import { t } from '../../i18n/langue';
import EnteteFiche from '../menage/EnteteFiche';

import FilCommentaires from './FilCommentaires';
import MessageVocal from './MessageVocal';
import PiedPanne from './PiedPanne';

/**
 * La fiche d'une panne — cible des pushes (`/maintenance/:issueId`).
 *
 * La photo en grand d'abord (c'est elle qui dit la panne), le message vocal
 * traduit, puis qui, où, quand ; le fil de commentaires ; et au pied le geste du
 * moment : « Je m'en occupe », puis commencer / pause / résolu.
 */
export default function FichePanne() {
    const navigate = useNavigate();
    const { issueId } = useParams<{ issueId: string }>();
    const { currentFacility, user } = useStateContext();
    const { hasPermission } = usePermissions();
    const { data: panne, isLoading, isError, refetch } = usePanne(currentFacility?.id ?? null, Number(issueId));
    const [message, setMessage] = useState<string | null>(null);
    const traductions = useTraductionsMetier([panne?.category_name]);
    const retour = () => navigate('/maintenance');
    const entete = (titre: string, sousTitre?: string) => (
        <EnteteFiche titre={titre} sousTitre={sousTitre} onRetour={retour} libelleRetour={t('maintenance.retour')} />
    );

    if (!panne) {
        const echec = echecSansDonnee({ isError, data: panne });
        return (
            <Screen entete={entete(t('maintenance.titre'))}>
                <div className="flex flex-col gap-3 px-4 py-4">
                    {isLoading && (
                        <Text variante="petit" ton="attenue">
                            {t('commun.chargement')}
                        </Text>
                    )}
                    {echec && <EtatErreur texte={t('maintenance.fiche.introuvable')} onReessayer={() => void refetch()} />}
                </div>
            </Screen>
        );
    }

    const aMoi = !!user?.id && panne.assigned_to_user_id === user.id;
    const peutChangerStatut = aMoi || hasPermission('housekeeping.assign');
    const urgent = panne.priority === 'critical';
    const sousTitre = [traductions.libelle(panne.category_name) ?? panne.category_name, libelleStatutPanne(panne.status)].filter(Boolean).join(' · ');
    const signalePar = panne.reported_by_name || panne.reporter_name;
    const infos = [
        signalePar ? t('maintenance.fiche.signalePar', { nom: signalePar }) : null,
        heurePanne(panne.created_at),
    ]
        .filter(Boolean)
        .join(' · ');

    return (
        <Screen
            entete={entete(lieuPanne(panne), sousTitre)}
            pied={<PiedPanne panne={panne} peutChangerStatut={peutChangerStatut} onFait={setMessage} />}
        >
            <div className="flex flex-col gap-4 px-4 py-4">
                {message && (
                    <div role="status" className="rounded-xl bg-main-color-light px-3 py-2">
                        <Text variante="petit" ton="marque">
                            {message}
                        </Text>
                    </div>
                )}

                {panne.photo_url && (
                    <img
                        src={panne.photo_url}
                        alt={t('maintenance.fiche.photo')}
                        className="max-h-[55vh] w-full rounded-2xl bg-white object-contain"
                    />
                )}

                {(urgent || panne.priority === 'high') && (
                    <span className={`self-start rounded-full px-3 py-1 ${urgent ? 'bg-alert-text-color' : 'bg-main-color-light'}`}>
                        <Text variante="petit" as="span" className={urgent ? 'font-semibold text-white' : ''}>
                            {libellePriorite(panne.priority)}
                        </Text>
                    </span>
                )}

                <MessageVocal
                    audioUrl={panne.audio_url}
                    transcription={panne.audio_transcript}
                    traduction={panne.audio_translation_fr}
                    creeLe={panne.created_at}
                />

                <section className="flex flex-col gap-1">
                    <Text variante="sousTitre" as="h2">
                        {panne.title}
                    </Text>
                    {panne.description && <Text className="whitespace-pre-line">{panne.description}</Text>}
                    {panne.precise_location && (
                        <Text variante="petit" ton="attenue">
                            {panne.precise_location}
                        </Text>
                    )}
                    <Text variante="petit" ton="attenue">
                        {infos}
                    </Text>
                    {panne.assigned_to_name && (
                        <Text variante="petit" ton="marque">
                            {aMoi
                                ? t('maintenance.fiche.vousVousEnOccupez')
                                : t('maintenance.fiche.prisEnChargePar', { nom: panne.assigned_to_name })}
                        </Text>
                    )}
                </section>

                <FilCommentaires issueId={panne.id} commentaires={panne.comments ?? []} />
            </div>
        </Screen>
    );
}
